import React, {
  useState,
  useEffect,
  Fragment,
  useRef,
  useCallback,
} from 'react';
import { connect, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import GoogleMap from 'google-map-react';
import { getUserLocation, getUserSelectedLanguage } from 'reducers/users';
import { getRegionCentreOfGravity } from 'reducers/region';
import { distressMap, normalMap } from './map.js';
import Config from 'config';
import { extendedMaps } from './additionalGoogleInstance';
import Marker from './Marker';
import Polyline from './Polyline';
import Ellipse from './Ellipse';
import Polygon from './Polygon';
import Drawing from './Drawing';
import CustomMapControl from './CustomControl.js';
import Rectangle from './Rectangle/index.js';
import COLORS from 'utils/constants/colors.js';
import { DEFAULT_ZOOM_LEVEL } from 'utils/constants/Map.js';
import { googleMaploader } from './loader';
import Layers from './Layers.js';
import SingleMarker from './Marker/SingleMarker.js';
import isEmpty from 'lodash/isEmpty';
import uniq from 'lodash/uniq';
import { useLocalStorage } from 'utils/helper/useLocalStorage.jsx';

const mapConfig = {
  // v: 3.35,
  key: Config.googleApiKey,
  language: 'en',
  libraries: ['geometry', 'drawing'], //load the geometry library for drawing eclipse.https://developers.google.com/maps/documentation/javascript/geometry
};
const extractLayers = markers => {
  if (isEmpty(markers)) return {};
  const layers = uniq(markers.map(m => m.layer)).reduce((prev, curr) => {
    if (!prev[curr]) {
      prev[curr] = true;
    }
    return prev;
  }, {});
  return layers;
};

const Map = ({
  drawing,
  distress,
  center,
  centerDefault,
  zoom,
  markers = [],
  detailOpen,
  mapInForm,
  addressLocationMarker,
  polylines,
  polygons = [],
  ellipses,
  colors = COLORS.ORANGE,
  selectedType,
  onMapClick,
  markerDragStart,
  markerDragEnd,
  markerDraggable,
  markerClick,
  setValues,
  mapTypeControl = true,
  isZoomEnabled,
  customMapControl,
  rectangles = [],
  panToLocation,
  highLightBoundary,
  setComponentMap,
  polygonsInForm, // it will disable the popup and mark the actual lat lng position
  ...rest
}) => {
  const ref = useRef();
  const selectedLang = useSelector(state => getUserSelectedLanguage(state));
  const layersRef = useRef(null);
  //------------stores all the markers-----------
  const markerRef = useRef({});
  const markerInfowindowRef = useRef({});
  //----------------------------
  const polygonRef = useRef({});
  const eventPolygonRef = useRef({});
  const polylineRef = useRef({});
  const eventPolylineRef = useRef({});

  const bootstrapKeyConfig = {
    ...mapConfig, // i18n needs 'vn' as language id and google maps needs VI
    language: selectedLang === 'vn' ? 'VI' : selectedLang.toUpperCase(),
    // region: selectedLang === 'vn' ? 'VI' : selectedLang.toUpperCase(),
  };
  const [mapsLoaded, setMapLoaded] = useState(false);
  const [newKey, setnewKey] = useState(selectedLang.id);

  const [map, setMap] = useState(null);
  const [maps, setMaps] = useState(null);

  const [layer, setLayer] = useLocalStorage('layer', {});

  const layerType = markers && [
    ...uniq([...markers, ...polygons].map(m => m?.layer)),
  ];

  const fetchLayerData = useCallback(() => {
    setLayer(prev => ({
      ...prev,
      ...extractLayers([...markers, ...polygons]),
      ...layer,
    }));
  }, [markers, setLayer]);
  useEffect(() => {
    if (distress) {
      //https://github.com/google-map-react/google-map-react/issues/76
      setnewKey('distress'); // this helps to clear old markers and only display emergency required markers
    }
    return () => {
      setnewKey('normal');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [distress]);
  useEffect(() => {
    fetchLayerData(); // only need to be called initially
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markers, polygons]);

  function onMapLoaded(map, maps) {
    setMap(map);
    setMaps(maps);
    setComponentMap &&
      typeof setComponentMap === 'function' &&
      setComponentMap(map);
    // should always be the last
    setMapLoaded(true);
  }

  const handleChange = e => {
    setLayer(prev => ({ ...prev, [e.target.name]: e.target.checked }));
  };

  useEffect(() => {
    let mapClick = null;

    if (maps && onMapClick && map) {
      mapClick = maps.event.addListener(map, 'click', e => onMapClick(e, map));
    }

    return () => {
      if (mapClick) {
        maps.event.removeListener(mapClick);
      }
      markerRef.current = {};
      markerInfowindowRef.current = {};
    };
  }, [maps, onMapClick, map]);

  useEffect(() => {
    if (map && maps) {
      if (isEmpty(highLightBoundary)) return;
      const bounds = new maps.LatLngBounds();
      for (const marker of highLightBoundary) {
        bounds.extend(new maps.LatLng(marker.lat, marker.lng));
      }
      map.fitBounds(bounds);
      if (isEmpty(panToLocation)) return;
      const { lat, lng } = panToLocation;
      if (lat && lng) {
        // if panToLocation lat and lng are available, always pan
        map.panTo({ lat, lng });
      }
    }
  }, [panToLocation, map, highLightBoundary, maps]);

  // if the distress is true and last of the personel or emergency is removed from the map
  // if (markers.length === 0 && distress) {
  //   for (const key in markerRef.current) {
  //     if (Object.hasOwnProperty.call(markerRef.current, key)) {
  //       const eachLayer = markerRef.current[key];
  //       eachLayer.forEach((f, index) => {
  //         // remove it frm the map and ref
  //         f.setMap(null);
  //         markerRef.current[key].splice(index, 1);
  //       });
  //     }
  //   }
  // }

  useEffect(() => {
    // This method removes all halo marker from all layers to render new halo markers
    const removeSpecificHaloMarkersFromLayer = () => {
      for (const key in markerRef.current) {
        if (Object.hasOwnProperty.call(markerRef.current, key)) {
          const eachLayer = markerRef.current[key];
          eachLayer.forEach((f, index) => {
            if (f.overlay) {
              // remove it frm the map and ref
              f.setMap(null);
              markerRef.current[key].splice(index, 1);
            }
          });
        }
      }
    };
    removeSpecificHaloMarkersFromLayer();
  }, [markers, detailOpen, markerRef]);

  // do not draw pins for the address not know to google so always check markers
  function afterMapLoads() {
    // let infoWindow = new maps.InfoWindow();
    // let layers = new maps.MVCObject();
    // check if any layer is switched off
    const switchedLayer = layer && Object.keys(layer).filter(f => !layer[f]);

    // check if any marker has overlay true
    const markerHasOverlay =
      markers && markers.filter(f => f.overlay).map(m => ({ layer: m?.layer }));

    return (
      <Fragment>
        {markers &&
          !mapInForm &&
          markers.length > 0 &&
          markers
            .filter(
              f =>
                (f.lat && f.lng) ||
                f.type === 'polygon' ||
                f.type === 'eclipse',
            )
            .map((marker, key) => (
              <Fragment key={key}>
                {marker?.type === undefined && (
                  <Marker
                    allMarkers={markers}
                    markerHasOverlay={
                      !isEmpty(markerHasOverlay) ? markerHasOverlay[0] : false
                    }
                    layers={layer}
                    switchedLayer={switchedLayer}
                    position={marker}
                    maps={maps}
                    map={map}
                    markerClick={markerClick}
                    markerDraggable={
                      typeof marker.markerDraggable === 'undefined'
                        ? markerDraggable
                        : marker.markerDraggable
                    }
                    markerDragStart={markerDragStart}
                    markerDragEnd={markerDragEnd}
                    text={marker.text}
                    icon={marker.icon}
                    iconOverlay={marker.overlay}
                    iconSelected={marker.selectedIcon}
                    iconSeqFillColor={marker.iconSeqFillColor}
                    iconTextType={marker.iconTextType}
                    infoWindow={new maps.InfoWindow()}
                    layersRef={layersRef}
                    markerRef={markerRef}
                    detailOpen={detailOpen}
                    markerInfowindowRef={markerInfowindowRef}
                    distress={distress}
                  />
                )}

                {marker?.type === 'polygon' && (
                  <Polygon
                    id={marker.id}
                    polygonData={marker}
                    editable={marker.editable}
                    draggable={marker.draggable}
                    setValues={setValues}
                    paths={marker.paths}
                    color={marker.color}
                    isFilled={marker.isFilled}
                    map={map}
                    maps={maps}
                    mode={marker.mode}
                    strokeOpacity={marker.strokeOpacity}
                    strokeWeight={marker.strokeWeight}
                    infoWindow={new maps.InfoWindow()}
                    polygonRef={eventPolygonRef}
                    zIndex={marker.zIndex}
                    {...(marker.onPolygonClick && {
                      onPolygonClick: marker.onPolygonClick,
                    })}
                    infoWindowData={marker}
                    polygonsInForm={polygonsInForm}
                    layers={layer}
                    switchedLayer={switchedLayer}
                    layersRef={layersRef}
                  />
                )}
                {marker?.type === 'polygon' && marker.polylineConfig && (
                  <Polyline
                    dashed
                    id={marker.id}
                    lineConfig={marker.polylineConfig}
                    strokeColor={marker.color}
                    geodesic={marker.polylineConfig.geodesic}
                    map={map}
                    maps={maps}
                    content={marker.polylineConfig.text}
                    markers={marker.paths}
                    layers={layer}
                    switchedLayer={switchedLayer}
                    polylineRef={eventPolylineRef}
                    polygonData={marker}
                    distress={distress}
                    detailOpen={detailOpen}
                  />
                )}
                {marker?.type === 'eclipse' && (
                  <Ellipse
                    ellipse={marker}
                    map={map}
                    maps={maps}
                    setValues={setValues}
                    polygonRef={eventPolygonRef}
                    infoWindowData={marker}
                    infoWindow={new maps.InfoWindow()}
                    layers={layer}
                    switchedLayer={switchedLayer}
                    layersRef={layersRef}
                    mapInForm={mapInForm}
                    polygonsInForm={polygonsInForm}
                    polygonData={marker}
                    polylineRef={eventPolylineRef}
                    {...(marker.onPolygonClick && {
                      onPolygonClick: marker.onPolygonClick,
                    })}
                  />
                )}
              </Fragment>
            ))}
        {markers &&
          mapInForm &&
          markers.length > 0 &&
          markers
            .filter(f => f.lat && f.lng)
            .map((marker, key) => (
              <SingleMarker
                key={key}
                layerType={layerType}
                markerHasOverlay={markerHasOverlay}
                layers={layer}
                switchedLayer={switchedLayer}
                position={marker}
                maps={maps}
                map={map}
                markerClick={markerClick}
                markerDraggable={
                  typeof marker.markerDraggable === 'undefined'
                    ? markerDraggable
                    : marker.markerDraggable
                }
                markerDragStart={markerDragStart}
                markerDragEnd={markerDragEnd}
                text={marker.text}
                icon={marker.icon}
                iconOverlay={marker.overlay}
                iconSelected={marker.selectedIcon}
                iconSeqFillColor={marker.iconSeqFillColor}
                iconTextType={marker.iconTextType}
                infoWindow={new maps.InfoWindow()}
                layersRef={layersRef}
                markerRef={markerRef}
              />
            ))}
        {addressLocationMarker &&
        addressLocationMarker.lat &&
        addressLocationMarker.lng ? (
          <SingleMarker
            position={addressLocationMarker}
            maps={maps}
            map={map}
            icon={addressLocationMarker ? addressLocationMarker.icon : null}
          />
        ) : null}

        {ellipses.length > 0 &&
          ellipses.map((ellipse, index) => (
            <Ellipse
              key={index}
              ellipse={ellipse}
              map={map}
              maps={maps}
              setValues={setValues}
              mapInForm={mapInForm}
              polygonsInForm={polygonsInForm}
            />
          ))}
        {polygons.length > 0 &&
          polygons.map((polygon, index) => (
            <Fragment key={index}>
              <Polygon
                id={polygon.id}
                polygonData={polygon}
                editable={polygon.editable}
                draggable={polygon.draggable}
                setValues={setValues}
                paths={polygon.paths}
                color={polygon.color}
                isFilled={polygon.isFilled}
                map={map}
                maps={maps}
                mode={polygon.mode}
                strokeOpacity={polygon.strokeOpacity}
                strokeWeight={polygon.strokeWeight}
                infoWindow={new maps.InfoWindow()}
                polygonRef={polygonRef}
                zIndex={polygon.zIndex}
                {...(polygon.onPolygonClick && {
                  onPolygonClick: polygon.onPolygonClick,
                })}
                infoWindowData={polygon}
                polygonsInForm={polygonsInForm}
                layers={layer}
                switchedLayer={switchedLayer}
                layersRef={layersRef}
              />
              {polygon.polylineConfig && (
                <Polyline
                  dashed
                  id={polygon.id}
                  lineConfig={polygon.polylineConfig}
                  strokeColor={polygon.color}
                  geodesic={polygon.polylineConfig.geodesic}
                  map={map}
                  maps={maps}
                  content={polygon.polylineConfig.text}
                  markers={polygon.paths}
                  layers={layer}
                  switchedLayer={switchedLayer}
                  polylineRef={polylineRef}
                  polygonData={polygon}
                  distress={distress}
                  detailOpen={detailOpen}
                />
              )}
            </Fragment>
          ))}
        {rectangles.length > 0 &&
          rectangles.map((rectangle, index) => (
            <Fragment key={index}>
              <Rectangle
                map={map}
                maps={maps}
                setValues={setValues}
                editable={rectangle.editable}
                draggable={rectangle.draggable}
                isFilled={rectangle.isFilled}
                color={rectangle.color}
                mode={rectangle.mode}
                strokeOpacity={rectangle.strokeOpacity}
                strokeWeight={rectangle.strokeWeight}
                paths={rectangle.paths}
              />
              {rectangle.polylineConfig && (
                <Polyline
                  dashed
                  lineConfig={rectangle.polylineConfig}
                  strokeColor={rectangle.color}
                  geodesic={rectangle.polylineConfig.geodesic}
                  map={map}
                  maps={maps}
                  content={rectangle.polylineConfig.text}
                  markers={rectangle.paths}
                  distress={distress}
                  detailOpen={detailOpen}
                />
              )}
            </Fragment>
          ))}

        <Polyline
          // dashed
          dashed={polylines.dashed}
          strokeColor={polylines.strokeColor}
          map={map}
          maps={maps}
          content={polylines.text}
          markers={polylines.markers}
          strokeWeight={polylines.strokeWeight}
          distress={distress}
          detailOpen={detailOpen}
          polygonData={{ layer: polylines.layer }}
        />
        {/* <Polyline
          dashed={true}
          strokeColor="#939598"
          map={map}
          maps={maps}
          markers={
            polylines?.markers?.length > 1
              ? [
                  polylines.markers[polylines.markers.length - 1],
                  polylines.markers[0],
                ]
              : []
          }
        /> */}
      </Fragment>
    );
  }

  function createMapOptions() {
    return {
      styles: distress ? distressMap : normalMap,
      fullscreenControl: true,
      zoom: 18,
      mapTypeControl,
      streetViewControl: false,
      zoomControl: false,
      clickableIcons: false,
      gestureHandling: isZoomEnabled ? 'greedy' : 'cooperative',
    };
  }

  function involvesDrawing() {
    return (
      colors &&
      selectedType && (
        <Drawing
          maps={maps}
          map={map}
          colors={colors}
          selectedType={selectedType}
          {...rest}
        />
      )
    );
  }

  useEffect(() => {
    if (!ref.current) {
      ref.current = true;
    } else {
      // if the component updated
      setnewKey(selectedLang);
    }
  }, [selectedLang]);

  //Order of centring. Fall-through if not available:
  // selected item CoG
  // User location
  // Region CoG
  // 0,0

  const getCentre = () => {
    if (!center || (center?.lat === 0 && center?.lng === 0)) {
      return centerDefault;
    } else if (
      !centerDefault ||
      (centerDefault?.lat === 0 && centerDefault?.lng === 0)
    ) {
      return { lat: 0, lng: 0 };
    }
    return center; // center of gravity of a marked area like a site or zone if any
  };

  return (
    <>
      {!mapInForm && !isEmpty(layer) && !isEmpty(layerType) && !distress && (
        <Layers
          handleChange={handleChange}
          value={layer}
          layerType={layerType}
        />
      )}
      <GoogleMap
        key={newKey} // if you want to reset the google maps then reset this key to a new value
        googleMapLoader={googleMaploader} // this helps to set the prefered language
        bootstrapURLKeys={bootstrapKeyConfig}
        center={getCentre()}
        defaultZoom={zoom}
        yesIWantToUseGoogleMapApiInternals
        options={createMapOptions}
        onGoogleApiLoaded={({ map, maps }) =>
          onMapLoaded(map, extendedMaps(maps))
        }
      >
        {mapsLoaded && afterMapLoads()}
        {mapsLoaded && drawing && involvesDrawing()}
        {customMapControl && (
          <CustomMapControl map={map} maps={maps}>
            {customMapControl}
          </CustomMapControl>
        )}
      </GoogleMap>
    </>
  );
};

Map.defaultProps = {
  zoom: DEFAULT_ZOOM_LEVEL,
  markers: [],
  polylines: {
    markers: [],
  },
  ellipses: [],
  polygons: [],
  isZoomEnabled: true,
};

Map.propTypes = {
  draggable: PropTypes.bool,
  editable: PropTypes.bool,
  setValues: PropTypes.func,
  markerDrag: PropTypes.func,
  drawing: PropTypes.bool,
  center: PropTypes.object,
  centerDefault: PropTypes.object,
  zoom: PropTypes.number,
  markers: PropTypes.array,
  polylines: PropTypes.shape({
    text: PropTypes.string,
    markers: PropTypes.array,
    dashed: PropTypes.bool,
    strokeColor: PropTypes.string,
    strokeWeight: PropTypes.number,
    layer: PropTypes.string,
  }),
  distress: PropTypes.bool,
  marker: PropTypes.object,
  ellipses: PropTypes.array,
  polygons: PropTypes.array,
  colors: PropTypes.string,
  selectedType: PropTypes.string,
  onMapClick: PropTypes.func,
  markerDragStart: PropTypes.func,
  markerDragEnd: PropTypes.func,
  markerClick: PropTypes.func,
  markerDraggable: PropTypes.bool,
  trip: PropTypes.object,
  mapTypeControl: PropTypes.bool,
  mapInForm: PropTypes.bool,
  isZoomEnabled: PropTypes.bool,
  polygonsInForm: PropTypes.bool,
  detailOpen: PropTypes.bool,
  customMapControl: PropTypes.node,
  rectangles: PropTypes.array,
  highLightBoundary: PropTypes.array,
  panToLocation: PropTypes.object,
  setComponentMap: PropTypes.func,
  addressLocationMarker: PropTypes.shape({
    icon: PropTypes.string,
    lat: PropTypes.number,
    lng: PropTypes.number,
  }),
};

const mapStateToProps = state => ({
  centerDefault: getUserLocation(state) || getRegionCentreOfGravity(state),
});

export default connect(mapStateToProps, null)(Map);
